/**
 * Live2D Model Manager
 * Extracted and adapted from Phantasy model.ts implementations
 */

import { SDKEventEmitter } from '../core/EventEmitter';
import { ExpressionManager } from './ExpressionManager';
import { ParameterMapper } from './ParameterMapper';
import { OrbitControls } from './OrbitControls';
import { createLogger } from '../utils/logger';

import type { Live2DModelConfig, FeatureConfig } from '../types/config';
import type { Live2DModel, PIXIApplication } from '../types/live2d';

export class ModelManager extends SDKEventEmitter {
  private logger: ReturnType<typeof createLogger>;
  private config: Live2DModelConfig;
  private features: FeatureConfig;
  private model: Live2DModel | null = null;
  private app: PIXIApplication | null = null;
  private expressionManager: ExpressionManager;
  private parameterMapper: ParameterMapper;
  private orbitControls: OrbitControls | null = null;
  private container: HTMLElement | null = null;
  private cleanupFunctions: (() => void)[] = [];

  constructor(modelConfig: Live2DModelConfig, features: FeatureConfig) {
    super();
    this.logger = createLogger('ModelManager');
    this.config = modelConfig;
    this.features = features;

    // DEBUG: Log the config received
    this.logger.info('ModelManager initialized with config:', {
      path: this.config.path,
      hasExpressions: !!this.config.expressions,
      expressionKeys: this.config.expressions ? Object.keys(this.config.expressions) : [],
      expressions: this.config.expressions,
    });

    this.expressionManager = new ExpressionManager();
    this.parameterMapper = new ParameterMapper();
  }

  async initialize(container: HTMLElement): Promise<void> {
    try {
      this.logger.info('Initializing ModelManager...');
      this.container = container;

      // Check for PIXI and Live2D availability
      if (!window.PIXI || !window.PIXI.live2d) {
        throw new Error('PIXI.js or Live2D plugin not available. Please ensure they are loaded.');
      }

      // Clean up any existing model
      this.cleanup();

      // Create PIXI application
      await this.createPixiApplication();

      // Load Live2D model
      await this.loadModel();

      // Set up model features
      this.setupModelFeatures();

      // Set up responsive handling
      if (this.features.responsivePositioning) {
        this.setupResponsiveHandling();
      }

      this.logger.info('ModelManager initialized successfully');
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('ModelManager initialization failed:', err);
      this.emit('error', err);
      throw err;
    }
  }

  setExpression(expression: string): void {
    if (!this.model) {
      this.logger.warn('Cannot set expression: model not loaded');
      return;
    }

    try {
      this.expressionManager.setExpression(this.model, expression, this.config.expressions);
      this.emit('expressionChanged', expression);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.logger.error('Failed to set expression:', err);
      this.emit('error', err);
    }
  }

  getModel(): Live2DModel | null {
    return this.model;
  }

  getParameterMapper(): ParameterMapper {
    return this.parameterMapper;
  }

  updatePosition(x?: number, y?: number): void {
    if (!this.model || !this.container) return;

    const finalX = x ?? this.config.position?.x ?? 0.5;
    const finalY = y ?? this.config.position?.y ?? 0.5;

    this.model.x = this.container.clientWidth * finalX;
    this.model.y = this.container.clientHeight * finalY;

    this.logger.debug(`Model positioned at (${this.model.x}, ${this.model.y})`);
  }

  updateScale(scale?: number): void {
    if (!this.model) return;

    const finalScale = scale ?? this.config.scale ?? 0.3;
    this.model.scale.set(finalScale);

    this.logger.debug(`Model scale set to ${finalScale}`);
  }

  // Orbit Controls Public API
  enableOrbitControls(): void {
    if (this.orbitControls) {
      this.orbitControls.enable();
    } else {
      this.logger.warn('Orbit controls not initialized. Enable in features config.');
    }
  }

  disableOrbitControls(): void {
    if (this.orbitControls) {
      this.orbitControls.disable();
    }
  }

  setZoom(zoom: number): void {
    if (this.orbitControls) {
      this.orbitControls.setZoom(zoom);
    } else {
      this.logger.warn('Orbit controls not available. Enable in features config.');
    }
  }

  setPan(x: number, y: number): void {
    if (this.orbitControls) {
      this.orbitControls.setPan(x, y);
    } else {
      this.logger.warn('Orbit controls not available. Enable in features config.');
    }
  }

  resetOrbitControls(): void {
    if (this.orbitControls) {
      this.orbitControls.reset();
    } else {
      this.logger.warn('Orbit controls not available. Enable in features config.');
    }
  }

  getOrbitControlsState(): { zoom: number; panX: number; panY: number } | null {
    if (this.orbitControls) {
      return this.orbitControls.getState();
    }
    return null;
  }

  destroy(): void {
    this.logger.info('Destroying ModelManager...');

    try {
      this.cleanup();
      this.removeAllListeners();
      this.logger.info('ModelManager destroyed');
    } catch (error) {
      this.logger.error('Error during ModelManager destruction:', error);
    }
  }

  // Private methods

  private async createPixiApplication(): Promise<void> {
    if (!this.container) throw new Error('Container not set');

    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;

    this.app = new window.PIXI!.Application({
      view: this.createCanvas(),
      autoStart: true,
      backgroundColor: 0x00000000,
      transparent: true,
      width,
      height,
      antialias: true,
      powerPreference: 'high-performance',
    } as any);

    // Store reference globally (matching your existing pattern)
    window.live2dApp = this.app;

    this.logger.info(`PIXI Application created (${width}x${height})`);
  }

  private createCanvas(): HTMLCanvasElement {
    if (!this.container) throw new Error('Container not set');

    // Remove any existing canvas
    const existingCanvas = this.container.querySelector('canvas');
    if (existingCanvas) {
      existingCanvas.remove();
    }

    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    this.container.appendChild(canvas);

    return canvas;
  }

  private async loadModel(): Promise<void> {
    if (!this.app) throw new Error('PIXI Application not created');

    this.logger.info(`Loading Live2D model from: ${this.config.path}`);

    try {
      this.model = await window.PIXI!.live2d.Live2DModel.from(this.config.path);

      // Configure model
      this.model.scale.set(this.config.scale || 0.3);
      this.model.anchor.set(0.5, 0.5);
      this.model.visible = true;

      // Disable built-in interactions
      this.disableBuiltInTracking();

      // Add to stage
      this.app.stage.addChild(this.model);

      // Position model
      this.updatePosition();

      // Store global reference (matching your existing pattern)
      window.live2dModel = this.model;

      this.logger.info('Live2D model loaded and configured successfully');
      this.emit('modelLoaded', this.model);
    } catch (error) {
      throw new Error(
        `Failed to load Live2D model: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  private disableBuiltInTracking(): void {
    if (!this.model) return;

    this.logger.info('Disabling built-in Live2D tracking and movements');

    // Disable focus/mouse tracking
    if (this.model.focus) {
      this.model.focus = () => {
        // Prevent automatic mouse tracking
      };
    }

    // Disable interactive mode
    this.model.interactive = false;
    this.model.interactiveChildren = false;

    // Remove event listeners
    if (this.model.removeAllListeners) {
      this.model.removeAllListeners();
    }
  }

  private setupModelFeatures(): void {
    console.log('ModelManager: setupModelFeatures called', {
      model: !!this.model,
      features: this.features,
    });

    if (!this.model) return;

    if (this.features.breathing) {
      this.setupBreathingAnimation();
    }

    // Set up mouse tracking OR orbit controls (not both - they conflict)
    if (this.features.orbitControls?.enabled) {
      console.log(
        'ModelManager: Orbit controls enabled - skipping mouse tracking to avoid conflicts'
      );
      this.logger.info('Orbit controls enabled - skipping mouse tracking to avoid conflicts');
      this.setupOrbitControls();
    } else if (this.features.mouseTracking) {
      console.log('ModelManager: Mouse tracking enabled instead of orbit controls');
      this.setupMouseTracking();
    } else {
      console.log('ModelManager: Neither orbit controls nor mouse tracking enabled');
    }

    // Set default expression if specified
    const expressionKeys = this.config.expressions ? Object.keys(this.config.expressions) : [];

    // DEBUG: Log what's in expressions
    this.logger.info('setupModelFeatures - expressions config:', {
      hasExpressions: !!this.config.expressions,
      expressionKeys: expressionKeys,
      defaultValue: this.config.expressions?.['default'],
      fullExpressions: this.config.expressions,
    });

    // Set default expression during initialization
    // CRITICAL FIX: This ensures expressions work on page refresh
    this.logger.info('setupModelFeatures - Setting default expression during init');

    const defaultExpressionKey =
      this.config.expressions && 'default' in this.config.expressions
        ? 'default'
        : expressionKeys.length > 0
          ? expressionKeys[0]
          : null;

    if (defaultExpressionKey) {
      // Add a small delay to ensure model is fully ready
      setTimeout(() => {
        this.logger.info(`Setting default expression: ${defaultExpressionKey}`);
        this.setExpression(defaultExpressionKey);
      }, 100);
    }
  }

  private setupBreathingAnimation(): void {
    if (!this.model) return;

    let animationFrame: number;
    let time = 0;

    const animate = () => {
      if (!this.model || !this.model.internalModel) return;

      time += 0.02;
      const breathValue = Math.sin(time) * 0.03; // Very subtle breathing

      // Apply to breath parameter if it exists
      const breathIndex = this.parameterMapper.findParameter(this.model, [
        'ParamBreath',
        'PARAM_BREATH',
        'Param_Breath',
      ]);

      if (breathIndex !== -1) {
        this.model.internalModel.coreModel.setParameterValueByIndex(breathIndex, breathValue);
      }

      animationFrame = requestAnimationFrame(animate);
    };

    animate();

    // Store cleanup function
    this.cleanupFunctions.push(() => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    });

    this.logger.debug('Breathing animation setup complete');
  }

  private setupMouseTracking(): void {
    if (!this.model || !this.container) return;

    this.logger.info('Setting up mouse tracking');

    const trackingStrength = 30;
    const smoothing = 0.2;

    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    const mouseMoveHandler = (e: MouseEvent) => {
      if (!this.container) return;

      const rect = this.container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const mouseX = (e.clientX - centerX) / (window.innerWidth / 2);
      const mouseY = (e.clientY - centerY) / (window.innerHeight / 2);

      targetX = mouseX * trackingStrength;
      targetY = mouseY * trackingStrength;
    };

    let trackingFrame: number;
    const updateTracking = () => {
      if (!this.model) return;

      currentX += (targetX - currentX) * smoothing;
      currentY += (targetY - currentY) * smoothing;

      // Apply to angle parameters
      this.parameterMapper.setParameterIfExists(
        this.model,
        ['ParamAngleX', 'PARAM_ANGLE_X'],
        currentX
      );
      this.parameterMapper.setParameterIfExists(
        this.model,
        ['ParamAngleY', 'PARAM_ANGLE_Y'],
        -currentY
      );

      trackingFrame = requestAnimationFrame(updateTracking);
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    updateTracking();

    // Store cleanup functions
    this.cleanupFunctions.push(() => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      if (trackingFrame) {
        cancelAnimationFrame(trackingFrame);
      }
    });
  }

  private setupOrbitControls(): void {
    console.log('ModelManager: setupOrbitControls called', {
      model: !!this.model,
      app: !!this.app,
      container: !!this.container,
      containerID: this.container?.id,
      orbitControlsConfig: this.features.orbitControls,
    });

    if (!this.model || !this.app || !this.container) {
      console.log('ModelManager: Missing requirements for orbit controls');
      return;
    }

    this.logger.info('Setting up orbit controls');

    try {
      console.log('ModelManager: Creating OrbitControls instance');
      // Create orbit controls with user configuration
      this.orbitControls = new OrbitControls(
        this.app,
        this.model,
        this.container,
        this.features.orbitControls || {}
      );

      // Store cleanup function
      this.cleanupFunctions.push(() => {
        if (this.orbitControls) {
          this.orbitControls.destroy();
          this.orbitControls = null;
        }
      });

      this.logger.info('Orbit controls initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize orbit controls:', error);
    }
  }

  private setupResponsiveHandling(): void {
    if (!this.app || !this.container) return;

    const handleResize = () => {
      if (!this.app || !this.container) return;

      const width = this.container.clientWidth || window.innerWidth;
      const height = this.container.clientHeight || window.innerHeight;

      // Resize PIXI renderer
      if ((this.app as any).renderer) {
        (this.app as any).renderer.resize(width, height);
      }

      // Update model position
      this.updatePosition();
    };

    // Debounce resize handler
    let resizeTimeout: number;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', debouncedResize);

    // Store cleanup function
    this.cleanupFunctions.push(() => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', debouncedResize);
      clearTimeout(resizeTimeout);
    });
  }

  private cleanup(): void {
    // Run all cleanup functions
    this.cleanupFunctions.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        this.logger.error('Error in cleanup function:', error);
      }
    });
    this.cleanupFunctions = [];

    // Cleanup model
    if (this.model) {
      try {
        this.model.destroy();
      } catch (error) {
        this.logger.error('Error destroying model:', error);
      }
      this.model = null;
      window.live2dModel = null;
    }

    // Cleanup PIXI app
    if (this.app) {
      try {
        this.app.destroy(true);
      } catch (error) {
        this.logger.error('Error destroying PIXI app:', error);
      }
      this.app = null;
      window.live2dApp = null;
    }
  }
}
